import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import { useTenantProfileUpdate } from "@/hooks/useAdminDashboardService";
import { useTenantById } from "@/hooks/useTenantDashboardService";
import { toast } from "react-toastify";
import type { StepData } from "@/types/companyProfile";
import CompanyBasics from "./CompanyBasics";
import BusinessEcosystem from "./BusinessEcosystem";
import MarketLandscape from "./MarketLandscape";
import type { TenantProfilePayload } from "@/types/tenantService";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

const ReviewConfirm: React.FC<{ formData: TenantProfilePayload }> = ({
  formData,
}) => {
  const renderList = (label: string, values: string[]) => (
    <div className="mb-4">
      <h4 className="text-md font-semibold text-white mb-1">{label}:</h4>
      {values.length > 0 ? (
        <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
          {values.map((val, idx) => (
            <li key={idx}>{val}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400 italic">No data provided</p>
      )}
    </div>
  );

  return (
    <div className="text-white space-y-4">
      <h3 className="text-lg font-semibold">Review & Confirm</h3>

      {renderList("Competitors", formData.competitors)}
      {renderList("Unique Selling Points", formData.uniqueSellingPoints)}
      {renderList("Target Industries", formData.targetIndustries)}
      {renderList("Partners", formData.partners)}
      {renderList("Sales Methodology", formData.salesMethodology)}
      {renderList("Products & Services", formData.productsServices)}
      {renderList("Value Proposition", formData.valueProposition)}

      {formData.whoweare && (
        <div>
          <h4 className="text-md font-semibold mb-1 text-white">Who We Are:</h4>
          <p className="text-sm text-gray-300 whitespace-pre-line">
            {formData.whoweare}
          </p>
        </div>
      )}

      {formData.reviewText && (
        <div>
          <h4 className="text-md font-semibold mb-1 text-white">
            Review Summary:
          </h4>
          <p className="text-sm text-gray-300 whitespace-pre-line">
            {formData.reviewText}
          </p>
        </div>
      )}
    </div>
  );
};

const CompanyProfile: React.FC = () => {
  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [errorSteps, setErrorSteps] = React.useState<number[]>([]);
  const [formErrors, setFormErrors] = React.useState<Record<string, string>>(
    {},
  );
  const [formData, setFormData] = React.useState<TenantProfilePayload>({
    competitors: [],
    uniqueSellingPoints: [],
    targetIndustries: [],
    partners: [],
    salesMethodology: [],
    reviewText: "",
    whoweare: "",
    valueProposition: [],
    productsServices: [],
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { mutate } = useTenantProfileUpdate();
  const tenantId = localStorage.getItem("tenantId") || "";
  const { data: tenantData } = useTenantById({
    tenantId,
    query: {
      include: "tenantProfile",
    },
  });

  // Store a ref to track if we've initialized from location state
  const initializedFromLocation = React.useRef(false);

  // Initialize formData from location.state or sessionStorage
  React.useEffect(() => {
    // Only initialize when tenantData is loaded, or if there's no location state, 
    // so we can merge scraped data with existing data.
    // If there is a location state, we wait for tenantData.
    if (location?.state && !initializedFromLocation.current && tenantData?.tenantProfile !== undefined) {
      initializedFromLocation.current = true;
      const ls = location.state;
      const existing = tenantData?.tenantProfile || {};
      const getArray = (val: any) => Array.isArray(val) ? val : [];

      // Merge location state with existing formData, preserving any user input
      setFormData((prev) => ({
        competitors: [...new Set([...getArray(existing.competitors), ...prev.competitors, ...getArray(ls.competitors)])],
        uniqueSellingPoints: [...new Set([...getArray(existing.uniqueSellingPoints), ...prev.uniqueSellingPoints, ...getArray(ls.uniqueSellingPoints)])],
        targetIndustries: [...new Set([...getArray(existing.targetIndustries), ...prev.targetIndustries, ...getArray(ls.targetIndustries)])],
        partners: [...new Set([...getArray(existing.partners), ...prev.partners, ...getArray(ls.partners)])],
        salesMethodology: [...new Set([...getArray(existing.salesMethodology), ...prev.salesMethodology, ...getArray(ls.salesMethodology)])],
        reviewText: ls.reviewText || prev.reviewText || existing.reviewText || "",
        whoweare: ls.whoweare || prev.whoweare || existing.about || "",
        valueProposition: [...new Set([...getArray(existing.valueProposition), ...prev.valueProposition, ...getArray(ls.valueProposition)])],
        productsServices: [...new Set([...getArray(existing.productsServices), ...prev.productsServices, ...getArray(ls.productsServices)])],
      }));
    } else if (!location?.state && !initializedFromLocation.current && tenantData?.tenantProfile !== undefined) {
      const saved = sessionStorage.getItem("tenantProfileFormData");
      if (saved) {
        try {
          setFormData(JSON.parse(saved));
        } catch (e) {
          console.error("Failed to parse saved form data", e);
        }
      } else {
        const existing = tenantData?.tenantProfile || {};
        const getArray = (val: any) => Array.isArray(val) ? val : [];
        setFormData({
          competitors: getArray(existing.competitors),
          uniqueSellingPoints: getArray(existing.uniqueSellingPoints),
          targetIndustries: getArray(existing.targetIndustries),
          partners: getArray(existing.partners),
          salesMethodology: getArray(existing.salesMethodology),
          reviewText: existing.reviewText || "",
          whoweare: existing.about || "",
          valueProposition: getArray(existing.valueProposition),
          productsServices: getArray(existing.productsServices),
        });
      }
      initializedFromLocation.current = true;
    }
  }, [location?.state, tenantData]);

  // Persist formData on changes
  React.useEffect(() => {
    if (initializedFromLocation.current) {
      sessionStorage.setItem("tenantProfileFormData", JSON.stringify(formData));
    }
  }, [formData]);

  // Enhanced setFormData wrapper to ensure persistence
  const updateFormData = React.useCallback(
    (
      updates:
        | Partial<TenantProfilePayload>
        | ((prev: TenantProfilePayload) => TenantProfilePayload),
    ) => {
      if (typeof updates === "function") {
        setFormData(updates);
      } else {
        setFormData((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    },
    [],
  );

  const steps: StepData[] = [
    {
      label: "Company Basics",
      description: (
        <CompanyBasics
          formData={formData}
          setFormData={updateFormData}
          data={location?.state}
          errors={formErrors}
          setFormErrors={setFormErrors}
        />
      ),
    },
    {
      label: "Market Landscape",
      description: (
        <MarketLandscape
          formData={formData}
          setFormData={updateFormData}
          data={location?.state}
          errors={formErrors}
          setFormErrors={setFormErrors}
        />
      ),
    },
    {
      label: "Business Ecosystem",
      description: (
        <BusinessEcosystem
          formData={formData}
          setFormData={updateFormData}
          data={location?.state}
          errors={formErrors}
          setFormErrors={setFormErrors}
        />
      ),
    },
    {
      label: "Review & Confirm",
      description: <ReviewConfirm formData={formData} />,
    },
  ];

  const validateStep = (index: number) => {
    const errors: Record<string, string> = {};

    switch (index) {
      case 0:
        if (!formData.whoweare?.trim()) errors.whoweare = "About is required.";

        if (formData.productsServices.length === 0)
          errors.productsServices = "At least one product is required.";

        if (formData.valueProposition.length === 0)
          errors.valueProposition =
            "At least one value proposition is required.";
        break;

      case 1:
        if (formData.competitors.length === 0)
          errors.competitors = "Competitors are required.";

        if (formData.uniqueSellingPoints.length === 0)
          errors.uniqueSellingPoints = "Unique selling points are required.";

        if (formData.targetIndustries.length === 0)
          errors.targetIndustries = "Target industries are required.";
        break;

      case 2:
        if (formData.partners.length === 0)
          errors.partners = "Partners are required.";

        if (formData.salesMethodology.length === 0)
          errors.salesMethodology = "Sales methodology is required.";
        break;
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    return true;
  };

  const handleNext = () => {
    const isValid = validateStep(activeStep);
    if (!isValid) {
      if (!errorSteps.includes(activeStep)) {
        setErrorSteps((prev) => [...prev, activeStep]);
      }
      return;
    }
    setErrorSteps((prev) => prev.filter((step) => step !== activeStep));
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    // Clear errors for the current step when going back
    setFormErrors({});
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setErrorSteps([]);
    setFormErrors({});
  };

  const handleSubmit = () => {
    const validations = [0, 1, 2].map((i) => validateStep(i));
    const firstInvalidIndex = validations.findIndex((v) => !v);
    if (firstInvalidIndex !== -1) {
      setActiveStep(firstInvalidIndex);
      setErrorSteps((prev) =>
        Array.from(new Set([...prev, firstInvalidIndex])),
      );
      return;
    }

    const tenantId = localStorage.getItem("tenantId");
    console.error("Tenant ID:", tenantId);
    if (!tenantId) {
      toast.error("Tenant ID not found. Please log in again.");
      return;
    }

    const data = {
      id: tenantId,
      about: formData.whoweare,
      productsServices: formData.productsServices,
      tenantId,
      valueProposition: formData.valueProposition,
      competitors: formData.competitors,
      uniqueSellingPoints: formData.uniqueSellingPoints,
      partners: formData.partners,
      targetIndustries: formData.targetIndustries,
      salesMethodology: formData.salesMethodology,
      reviewText: formData.reviewText,
    };

    mutate(
      { id: tenantId, payload: data },
      {
        onSuccess: () => {
          sessionStorage.removeItem("tenantProfileFormData");
          navigate("/company-profile-info");
          toast.success("Profile updated successfully.");
        },
        onError: (error) => {
          console.error("Profile update failed:", error);
        },
      },
    );
  };

  const { isDark } = useDashboardTheme();

  return (
    <main className={"flex-1 p-4 md:p-6 mt-20 " + (isDark ? "" : "light-theme-dashboard")}>
      <div className="max-w-6xl mx-auto bg-[#211F2F] manual-profile-card rounded-2xl p-6 shadow-md">
        <Box sx={{ maxWidth: 800 }}>
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            sx={{
              ".MuiStepIcon-root": { color: "#B8F890" },
              ".MuiStepIcon-text": { fill: "#211F2F", fontWeight: "bold" },
              ".Mui-active .MuiStepIcon-root": { color: "#501b99" },
              ".Mui-active .MuiStepIcon-text": { fill: "#ffffff" },
              ".MuiStepIcon-root.Mui-completed": { color: "#000000" },
              ".MuiStepLabel-label.Mui-completed": { color: "black" },
              ".MuiStepIcon-root.Mui-completed path": { fill: "white" },
              ".MuiStepLabel-label": { color: "#B8F890", cursor: "pointer" },
            }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  onClick={() => {
                    setFormErrors({});
                    setActiveStep(index);
                  }}
                  StepIconProps={{
                    sx: errorSteps.includes(index) ? { color: "red" } : {},
                  }}
                >
                  <h1 className="text-1xl font-semibold poppins text-white">
                    {step.label}
                  </h1>
                </StepLabel>
                {activeStep === index && (
                  <StepContent>
                    <Box sx={{ color: "white" }}>{step.description}</Box>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() =>
                          index === steps.length - 1
                            ? handleSubmit()
                            : handleNext()
                        }
                        sx={{
                          mt: 1,
                          mr: 1,
                          backgroundColor: "#B8F890",
                          color: "#211F2F",
                          "&:hover": { backgroundColor: "#A0E275" },
                        }}
                      >
                        {index === steps.length - 1 ? "Finish" : "Continue"}
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        sx={{
                          mt: 1,
                          mr: 1,
                          color: "#B8F890",
                          borderColor: "#B8F890",
                          border: "1px solid",
                          "&:hover": {
                            backgroundColor: "#B8F890",
                            color: "#211F2F",
                          },
                        }}
                      >
                        Back
                      </Button>
                    </Box>
                  </StepContent>
                )}
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length && (
            <Paper
              square
              elevation={0}
              sx={{ p: 3, backgroundColor: "#211F2F", color: "#FFFFFF" }}
            >
              <Typography sx={{ color: "white" }}>
                All steps completed - you&apos;re finished
              </Typography>
              <Button
                onClick={handleReset}
                sx={{
                  mt: 1,
                  mr: 1,
                  backgroundColor: "#B8F890",
                  color: "#211F2F",
                  "&:hover": { backgroundColor: "#A0E275" },
                }}
              >
                Reset
              </Button>
            </Paper>
          )}
        </Box>
      </div>
    </main>
  );
};

export default CompanyProfile;
